import { useState, useEffect } from "react";
import * as APIModels from "../../../models";
import Select, { ActionMeta } from "react-select";
import AsyncSelect from "react-select/async";
import { DataService } from "../../../services/DataService";
import { ClientService } from "../../../services/ClientService";
import CaseTypeRuleModal from "./modals/CaseTypeRuleModal";

type MyOptionType = { label: string; value: string };

const filterOption = (option: MyOptionType, inputValue: string): boolean =>
    (
        option.label.toString().toLowerCase().match(inputValue.toLowerCase()) ||
        []
    ).length > 0;

export default function CaseDataInput({
    submission,
    onSaveCaseData,
}: {
    submission: APIModels.Submission;
    onSaveCaseData: (
        result: APIModels.CaseData,
        portalIdentifier: string
    ) => void;
}) {
    let options: MyOptionType[] = [];

    const [portalIdentifier, setPortalIdentifier] = useState(
        submission.portalIdentifier
    );

    const [state, setState] = useState(submission.portalIdentifier);

    const [completedCaseData, setCompletedCaseData] = useState(
        submission.caseData ?? new APIModels.CaseData()
    );

    const [counties, setCounties] = useState([] as APIModels.County[]);
    const [countyName, setCountyName] = useState("");

    const [countyOptions, setCountyOptions] = useState(options);
    const [divisionOptions, setDivisionOptions] = useState(options);
    const [caseTypeOptions, setCaseTypeOptions] = useState(options);
    const [caseSubtypeOptions, setCaseSubtypeOptions] = useState(options);
    const [damageCodeOptions, setDamageCodeOptions] = useState(options);
    const [remedyOptions, setRemedyOptions] = useState(options);

    const [ruleModalShow, setRuleModalShow] = useState(false);

    useEffect(() => {
        console.log(state);
        if (
            portalIdentifier != "PAL" &&
            portalIdentifier != "GO" &&
            portalIdentifier != undefined
        ) {
            DataService.getCountiesByPortal({ state: state! }).then(
                (resp) => {
                    options = resp.map((v, i) => {
                        return { value: v.id!, label: v.name! };
                    });
                    setCountyOptions(options);
                    setCounties(resp);
                },
                (error) => {
                    console.log(error);
                }
            );
        }
    }, []);

    useEffect(() => {
        if (completedCaseData.countyID != null) {
            setPortalIdentifier(
                counties.find((c) => c.id == completedCaseData.countyID)
                    ?.portalIdentifier
            );
        }
    }, [counties]);

    useEffect(() => {
        setPortalIdentifier(
            counties.find((c) => c.id == completedCaseData.countyID)
                ?.portalIdentifier
        );
        DataService.getDivisions({
            countyId: completedCaseData.countyID!,
        }).then(
            (resp) => {
                options = resp.map((v, i) => {
                    return { value: v.id!, label: v.name! };
                });
                setDivisionOptions(options);
            },
            (error) => {
                console.log(error);
            }
        );
    }, [completedCaseData.countyID]);

    useEffect(() => {
        ClientService.getDivision({
            body: {
                attorneyID: submission.attorneyID,
                countyID: completedCaseData.countyID!,
                division: completedCaseData.division,
                divisionAddress: completedCaseData.divisionAddress,
                claimAmount: completedCaseData?.claimAmount
                    ? Number(
                          completedCaseData?.claimAmount.replace(
                              /[^0-9.-]+/g,
                              ""
                          )
                      )
                    : completedCaseData?.smallClaimsAmount,
            },
        }).then(
            (resp) => {
                if (resp && resp.successful) {
                    setCompletedCaseData({
                        ...completedCaseData,
                        divisionID: resp?.data?.id,
                        division: resp?.data?.name,
                    });
                }
            },
            (error) => {
                console.log(error);
            }
        );
    }, [divisionOptions]);

    useEffect(() => {
        setCaseSubtypeOptions([]);
        DataService.getCaseTypes({
            divisionId: completedCaseData.divisionID!,
        }).then(
            (resp) => {
                options = resp
                    .filter((v) => v.active)
                    .map((v, i) => {
                        return { value: v.id!, label: v.description! };
                    });
                setCaseTypeOptions(options);
            },
            (error) => {
                console.log(error);
            }
        );
    }, [completedCaseData.divisionID]);

    useEffect(() => {
        ClientService.getCaseType({
            body: {
                attorneyID: submission.attorneyID,
                countyID: completedCaseData.countyID!,
                divisionID: completedCaseData.divisionID!,
                caseType: completedCaseData?.caseType,
                efmeCaseType: completedCaseData?.efmeCaseType,
                caseSubType: completedCaseData?.caseSubType,
                claimAmount: completedCaseData?.claimAmount
                    ? Number(
                          completedCaseData?.claimAmount.replace(
                              /[^0-9.-]+/g,
                              ""
                          )
                      )
                    : completedCaseData?.smallClaimsAmount,
            },
        }).then(
            (resp) => {
                if (resp && resp.successful) {
                    setCompletedCaseData({
                        ...completedCaseData,
                        caseTypeID: resp?.data?.caseTypeID,
                    });
                }
            },
            (error) => {
                console.log(error);
            }
        );
    }, [caseTypeOptions]);

    useEffect(() => {
        DataService.getCaseSubTypes({
            caseTypeId: completedCaseData.caseTypeID!,
        }).then(
            (resp) => {
                options = resp.map((v, i) => {
                    return { value: v.id!, label: v.name! };
                });
                setCaseSubtypeOptions(options);
            },
            (error) => {
                console.log(error);
            }
        );
    }, [completedCaseData.caseTypeID]);

    useEffect(() => {
        if (caseSubtypeOptions.length > 0) {
            ClientService.getCaseType({
                body: {
                    attorneyID: submission.attorneyID,
                    countyID: completedCaseData.countyID!,
                    divisionID: completedCaseData.divisionID!,
                    caseType: completedCaseData?.caseType,
                    efmeCaseType: completedCaseData?.efmeCaseType,
                    caseSubType: completedCaseData?.caseSubType,
                    claimAmount: completedCaseData?.claimAmount
                        ? Number(
                              completedCaseData?.claimAmount.replace(
                                  /[^0-9.-]+/g,
                                  ""
                              )
                          )
                        : completedCaseData?.smallClaimsAmount,
                },
            }).then(
                (resp) => {
                    if (resp && resp.successful) {
                        setCompletedCaseData({
                            ...completedCaseData,
                            caseSubTypeID: resp?.data?.id,
                        });
                    }
                },
                (error) => {
                    console.log(error);
                }
            );
        }
    }, [caseSubtypeOptions]);

    useEffect(() => {
        DataService.getDamagesCodes({
            divisionId: completedCaseData.divisionID!,
        }).then(
            (resp) => {
                options = resp.map((v, i) => {
                    return { value: v.id!, label: v.name! };
                });
                setDamageCodeOptions(options);
            },
            (error) => {
                console.log(error);
            }
        );
    }, [completedCaseData.divisionID]);

    useEffect(() => {
        DataService.getRemedyCodes({
            divisionId: completedCaseData.divisionID!,
        }).then(
            (resp) => {
                options = resp.map((v, i) => {
                    return { value: v.id!, label: v.name! };
                });
                setRemedyOptions(options);
            },
            (error) => {
                console.log(error);
            }
        );
    }, [completedCaseData.divisionID]);

    const onCountyChange = (
        value: MyOptionType | null,
        actionMeta: ActionMeta<MyOptionType>
    ) => {
        setCompletedCaseData({
            ...completedCaseData,
            countyID: value?.value,
            county: value?.label,
            divisionID: APIModels.EmptyGuid,
            division: "",
            caseTypeID: APIModels.EmptyGuid,
            caseSubTypeID: APIModels.EmptyGuid,
        });
        setPortalIdentifier(
            counties.find((c) => c.id == value?.value)?.portalIdentifier
        );
    };

    const onDivisionChange = (
        value: MyOptionType | null,
        actionMeta: ActionMeta<MyOptionType>
    ) => {
        console.log(`The division is ${value} in Child`);
        setCompletedCaseData({
            ...completedCaseData,
            divisionID: value?.value,
            division: value?.label,
        });
    };

    const onCaseTypeChange = (
        value: MyOptionType | null,
        actionMeta: ActionMeta<MyOptionType>
    ) => {
        setCompletedCaseData({
            ...completedCaseData,
            caseTypeID: value?.value,
            //caseType: value?.label,
        });
    };

    const onCaseSubtypeChange = (
        value: MyOptionType | null,
        actionMeta: ActionMeta<MyOptionType>
    ) => {
        setCompletedCaseData({
            ...completedCaseData,
            caseSubTypeID: value?.value,
            //caseSubType: value?.label,
        });
    };

    const onRemedyCodeChange = (
        value: MyOptionType | null,
        actionMeta: ActionMeta<MyOptionType>
    ) => {
        setCompletedCaseData({
            ...completedCaseData,
            damagesCodeID: value?.value,
        });
    };

    const onDamagesCodeChange = (
        value: MyOptionType | null,
        actionMeta: ActionMeta<MyOptionType>
    ) => {
        setCompletedCaseData({
            ...completedCaseData,
            damagesCodeID: value?.value,
        });
    };

    const onSaveClick = () => {
        if (onSaveCaseData)
            onSaveCaseData(
                completedCaseData,
                counties.find((c) => c.id == completedCaseData.countyID)
                    ?.portalIdentifier ?? portalIdentifier!
            );
    };

    const filterOption = (option: MyOptionType, inputValue: string): boolean =>
        (
            option.label
                .toString()
                .toLowerCase()
                .match(inputValue.toLowerCase()) || []
        ).length > 0;

    const openRuleModal = () => {
        /*
        if (
            completedCaseData.countyID != null ||
            completedCaseData.countyID != undefined ||
            completedCaseData.countyID != APIModels.EmptyGuid
        ) {
            setCountyName(
                counties.find((c) => c.id === completedCaseData.countyID)
                    ?.name ?? ""
            );
        } else {
            setCountyName(
                counties.find((c) => c.id === submission.caseData!.countyID)
                    ?.name ?? ""
            );
        }
        */

        setRuleModalShow(true);
    };

    return (
        <>
            <div className="row">
                <CaseTypeRuleModal
                    show={ruleModalShow}
                    attorneyId={submission.attorneyID ?? ""}
                    county={completedCaseData.county ?? ""}
                    division={completedCaseData.division}
                    csvCaseType={submission.caseData?.caseType ?? ""}
                    csvSubCaseType={submission.caseData?.caseSubType ?? ""}
                    efmeCaseType={submission.caseData?.efmeCaseType ?? ""}
                    caseType={completedCaseData.caseType ?? ""}
                    caseSubType={completedCaseData.caseSubType ?? ""}
                    onSave={() => setRuleModalShow(false)}
                    onClose={() => setRuleModalShow(false)}
                />
                <div className="col-8">
                    <div className="col-10 mb-3">
                        <label htmlFor="selCounty" className="form-label">
                            County/Court
                        </label>
                        <Select
                            id="selCounty"
                            name="selCounty"
                            value={countyOptions.filter(
                                (c) => c.value === completedCaseData.countyID
                            )}
                            filterOption={filterOption}
                            options={countyOptions}
                            onChange={onCountyChange}
                        />
                    </div>
                    <div className="col-10 mb-3">
                        <label htmlFor="selDivision" className="form-label">
                            Division
                        </label>

                        <Select
                            id="selDivision"
                            name="selDivision"
                            value={divisionOptions.filter(
                                (c) => c.value === completedCaseData.divisionID
                            )}
                            filterOption={filterOption}
                            options={divisionOptions}
                            onChange={onDivisionChange}
                        />
                    </div>
                    <div className="row">
                        {" "}
                        <div className="col-10 mb-3">
                            <label htmlFor="selCaseType" className="form-label">
                                Case Type
                            </label>

                            <Select
                                id="selCaseType"
                                name="selCaseType"
                                value={caseTypeOptions.filter(
                                    (c) =>
                                        c.value === completedCaseData.caseTypeID
                                )}
                                filterOption={filterOption}
                                options={caseTypeOptions}
                                onChange={onCaseTypeChange}
                            />
                        </div>
                        <div className="col" style={{ display: "none" }}>
                            <button
                                style={{
                                    display: "inline-block",
                                    position: "relative",
                                    top: "35%",
                                    left: "0%",
                                }}
                                tabIndex={-1}
                                className="btn btn-sm btn-primary mb-3"
                                type="button"
                                onClick={() => openRuleModal()}
                            >
                                +
                            </button>
                        </div>
                    </div>
                    {portalIdentifier != "GO" && (
                        <div className="col-10 mb-3">
                            <label
                                htmlFor="selCaseSubType"
                                className="form-label"
                            >
                                Case Subtype
                            </label>
                            <Select
                                id="selCaseSubtype"
                                name="selCaseSubtype"
                                value={caseSubtypeOptions.filter(
                                    (c) =>
                                        c.value ===
                                        completedCaseData.caseSubTypeID
                                )}
                                filterOption={filterOption}
                                options={caseSubtypeOptions}
                                onChange={onCaseSubtypeChange}
                            />
                        </div>
                    )}

                    {remedyOptions && remedyOptions.length > 0 && (
                        <div className="col-10 mb-3">
                            <label
                                htmlFor="selRemedyCodes"
                                className="form-label"
                            >
                                Procedure Remedy Codes
                            </label>
                            <Select
                                id="selRemedyCodes"
                                name="selRemedyCodes"
                                filterOption={filterOption}
                                options={remedyOptions}
                            />
                        </div>
                    )}
                    {damageCodeOptions && damageCodeOptions.length > 0 && (
                        <div className="col-10 mb-3">
                            <label
                                htmlFor="selDamagesCodes"
                                className="form-label"
                            >
                                Damages Codes
                            </label>
                            <Select
                                id="selDamagesCodes"
                                name="selDamagesCodes"
                                value={damageCodeOptions.filter(
                                    (c) =>
                                        c.value ===
                                        completedCaseData.damagesCodeID
                                )}
                                filterOption={filterOption}
                                options={damageCodeOptions}
                                onChange={onDamagesCodeChange}
                            />
                        </div>
                    )}
                </div>

                <div className="col-4">
                    <div className="mb-3">
                        <label className="form-label">County</label>
                        <div>{submission.caseData?.county}</div>
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Case Type</label>
                        <div id="csvCaseType">
                            {submission.caseData?.caseType}
                        </div>
                    </div>
                    <div className="mb-3">
                        <label className="form-label">EFME Case Type</label>
                        <div id="efmeCaseType">
                            {submission.caseData?.efmeCaseType}
                        </div>
                    </div>

                    <div className="mb-3">
                        <label className="form-label">Total Defendants</label>
                        <div>{submission.caseData?.totalDefendants}</div>
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Division</label>

                        <div id="csvDivision">
                            {submission.caseData?.division}
                        </div>
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Sub-Case Type</label>
                        <div id="csvCaseSubType">
                            {submission.caseData?.caseSubType}
                        </div>
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Matter Number</label>
                        <div>{submission.caseData?.clientMatterNumber}</div>
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Claim Amount</label>
                        <div>{submission.caseData?.claimAmount}</div>
                    </div>
                    <div className="mb-3">
                        <label className="form-label">
                            Small Claims Amount
                        </label>
                        <div>{submission.caseData?.smallClaimsAmount}</div>
                    </div>
                </div>
            </div>

            <div className="row mb-3">
                <button
                    className="btn btn-primary"
                    type="button"
                    onClick={onSaveClick}
                >
                    Save
                </button>
            </div>
        </>
    );
}
